import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { FilterSortDirection, getValues } from '@gov-nx/core/types';
import { toStringDate, today, useDateShape } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import type {
	DataRequest,
	MyVerificationsListFilterForm,
	MyVerificationsListFilter,
} from './context.types';
import { MyVerificationsListOrder } from './context.types';

export interface FormInstanceProps {
	code: PageCode;
	defaultValues: MyVerificationsListFilterForm;
}

export function FormInstance({
	code,
	defaultValues,
}: FormInstanceProps): FormDefinition<MyVerificationsListFilterForm> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { getDatePickerShape } = useDateShape();

	const formSchema = yup
		.object({
			obdobiOd: getDatePickerShape()
				.max(today(), tsn('form.validations.datum-obdobi.max'))
				.nullable(),
			obdobiDo: getDatePickerShape()
				.min(
					yup.ref('obdobiOd'),
					tsn('form.validations.datum-obdobi.starsi-nez-obdobi-od')
				)
				.max(today(), tsn('form.validations.datum-obdobi.max'))
				.nullable(),
			razeni: yup
				.string()
				.oneOf(getValues(MyVerificationsListOrder))
				.optional(),
			razeniSmer: yup.string().oneOf(getValues(FilterSortDirection)).optional(),
			hledat: yup.string().optional(),
		})
		.required();

	const formMethods = useGovForm<MyVerificationsListFilterForm>({
		formSchema,
		defaultValues,
	});
	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (
	formData: MyVerificationsListFilter
): DataRequest => {
	const order = (
		formData.razeniSmer === FilterSortDirection.DESC
			? '-' + formData.razeni
			: formData.razeni
	) as MyVerificationsListOrder;

	return {
		params: {
			razeni: order,
			obdobiOd: formData.obdobiOd ? toStringDate(formData.obdobiOd) : undefined,
			obdobiDo: formData.obdobiDo ? toStringDate(formData.obdobiDo) : undefined,
			start: formData.start,
			pocet: formData.pocet,
		},
	};
};
